<template>
  <teleport to="#sub-navigation__action-buttons">
    <div class="flex align-middle justify-center items-center gap-8 h-56">
      <GroupSubMenu @search="search" @filter="filter" @add-group="addGroup" />
    </div>
  </teleport>
  <div class="bg-white rounded-8 border border-gray-100 overflow-hidden">
    <SparkSmallTable :columns="columns" :rows="pagedRows" class="border-0" @sort="sort">
      <template #cell-name="{ row }">
        <div class="flex items-center gap-12">
          <!-- <SparkCheckbox :name="`select-${row.batch_pk}`" v-model="selectedRows[row.batch_pk]" /> -->
          <router-link
            :to="{ name: 'group-details', params: { id: row.batch_pk } }"
            class="text-14 font-medium text-gray-900 hover:text-primary-500 cursor-pointer"
          >
            {{ row.batch_name }}
          </router-link>
          <SparkBadge small v-if="row.prp_order_uid" custom="!cursor-default !m-0" class="mx-2" variant="success"
            ><div v-text="'PRP'"
          /></SparkBadge>
          <SparkBadge small v-if="row.order_finalized" custom="!cursor-default !m-0" class="mx-2" variant="outlined"
            ><div v-text="'Offer Requested'"
          /></SparkBadge>
          <SparkBadge
            v-if="row.prp_order_uid"
            small
            custom="!m-0"
            class="m-8 cursor-pointer"
            variant="outlined"
            @click="openOrderDetailsModal(row.prp_order_uid)"
            ><div v-text="'View details'"
          /></SparkBadge>
        </div>
      </template>
      <template #cell-number_of_parts="{ value }">
        <span class="text-14 text-gray-900">{{ value }}</span>
      </template>
      <template #cell-created_by="{ value }">
        <div class="flex items-center gap-8">
          <img src="@/assets/icons/user.svg" class="w-16 h-16" alt="User" />
          <span class="text-14 text-gray-900">{{ value }}</span>
        </div>
      </template>
      <template #cell-created_at="{ value }">
        <span class="text-13">{{ formatDate(value) }}</span>
      </template>
      <template #cell-actions="{ row }">
        <div class="relative flex justify-center">
          <DropdownMenu
            :list-options="dropdownOptions"
            title-icon="fas fa-ellipsis-h"
            close-click-style="always"
            :title-style="{ 'font-size': 'var(--15px)', padding: '1px 0px 0px 0px' }"
            @update-option="handleAction($event, row)"
          />
        </div>
      </template>
    </SparkSmallTable>
    <BatchUploadModal
      @batch-upload-complete="openBatch"
      :show="showBatchUploadModal"
      @close="showBatchUploadModal = false"
    />
    <div class="flex items-center justify-between px-24 py-12 bg-gray-0 border-t border-gray-50 text-13 text-gray-500">
      <div>
        <div class="text-13">
          <span class="text-gray-400" v-text="'Viewing'" />&nbsp;
          <span class="font-bold">{{ groupCountStart }} &nbsp;-&nbsp;{{ groupCountEnd }}</span>
          &nbsp;<span v-text="'of'" />&nbsp;
          <span class="font-bold">{{ batches.count }} &nbsp;</span>
          <span class="text-gray-400" v-text="'Groups'" />
        </div>
      </div>

      <TablePagination
        :current-page="currentPage"
        :max-page-number="totalPages"
        @update-current-page="updateCurrentPage($event)"
      />

      <OrderDetailsModal
        :order-details="currentOrderDetails"
        :combo-categories="currentOrderDetails.prp_combo_categories"
        :text-categories="currentOrderDetails.prp_text_categories"
        :show="showOrderDetailsModal"
        :load-order-details="loadOrderDetails"
        @close="closeOrderDetailsModal"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useStore, mapState, mapActions, mapMutations } from 'vuex';
import { useRouter } from 'vue-router';
import { notify } from '@/helpers';
import TablePagination from '@/components/Reusable/TablePagination.vue';
import SparkSmallTable from '@/components/SparkComponents/SparkSmallTable.vue';
import SparkCheckbox from '@/components/SparkComponents/SparkCheckbox.vue';
import SparkButtonIcon from '@/components/SparkComponents/SparkButtonIcon.vue';
import DropdownMenu from '@/components/Reusable/DropdownMenu.vue';
import GroupSubMenu from './GroupSubMenu.vue';
import BatchUploadModal from '@/views/Library/batch/components/BatchUploadModal.vue';
import SparkBadge from '@/components/SparkComponents/SparkBadge.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import { saveAs } from 'file-saver';
import { formatDate } from '@/helpers';
import OrderDetailsModal from '@/views/Library/batch/components/OrderDetailsModal.vue';

export default defineComponent({
  name: 'GroupTable',

  components: {
    SparkSmallTable,
    SparkCheckbox,
    SparkButtonIcon,
    DropdownMenu,
    GroupSubMenu,
    BatchUploadModal,
    SparkBadge,
    SparkInput,
    TablePagination,
    OrderDetailsModal,
  },

  computed: {
    ...mapState('application', ['axiosInstance']),
  },

  setup() {
    const store = useStore();
    const router = useRouter();

    const columns = ref([
      { key: 'name', title: 'Name', sortable: true },
      { key: 'parts', title: 'Parts', sortable: true },
      { key: 'by', title: 'By', sortable: true },
      { key: 'created_at', title: 'Created', sortable: true },
      { key: 'actions', title: '', sortable: false },
    ]);

    const currentOrderDetails = ref({});
    const showOrderDetailsModal = ref(false);
    const loadOrderDetails = ref(false);

    const dropdownOptions = ref([
      { text: 'View', value: 'view', icon: 'fas fa-eye', disabled: false },
      { text: 'Download', value: 'download', icon: 'fas fa-download', disabled: false },
      { text: 'Delete', value: 'delete', icon: 'fas fa-trash', disabled: false },
    ]);

    const selectedRows = ref({});
    const pageSize = computed(() => batches.value.per_page || 25);
    const currentPage = ref(1);
    const inputPage = ref(1);
    const showBatchUploadModal = ref(false);
    const searchTerm = ref('');
    const searchTimeout = ref(null);
    const partLibraryData = computed(() => store.state.partLibraryData);
    const batchParts = ref({});
    const selectedBatchId = ref('');
    const checkedParts = ref([]);
    const allOrderDetails = ref([]);
    const batchSortOrder = computed(() => store.state.batchSortOrder);
    const currentSort = ref('');
    const currentOrder = ref('');

    const axiosInstance = computed(() => store.state.application.axiosInstance);
    const user = computed(() => store.state.user);
    const batches = computed(() => store.state.batches);
    const fetchExcelListReportXLS = computed(() => store.state.fetchExcelListReportXLS);

    const totalPages = computed(() => batches.value.num_pages || 1);

    const groupCountStart = computed(() => (currentPage.value - 1) * pageSize.value + 1);
    const groupCountEnd = computed(() => groupCountStart.value + pagedRows.value.length - 1);

    const pagedRows = computed(() => {
      if (!batches.value.results) return [];

      return batches.value.results.map(batch => ({
        batch_pk: batch.batch_pk,
        batch_name: batch.batch_name,
        name: batch.batch_name,
        parts: batch.number_of_parts,
        by: batch.created_by,
        created_at: batch.created_at,
        order_finalized: batch.order_finalized,
        prp_order_uid: batch.prp_order_uid,
      }));
    });

    const startIdx = computed(() => (currentPage.value - 1) * pageSize.value);
    const rows = computed(() => batches.value.results);
    const endIdx = computed(() => Math.min(currentPage.value * pageSize.value, batches.value.count || 0));

    const performSearch = searchValue => {
      currentPage.value = 1;
      store.dispatch('fetchBatches', {
        page: currentPage.value,
        search: searchValue,
        sort: currentSort.value,
        order: currentOrder.value,
      });
    };

    const search = event => {
      searchTerm.value = event;

      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      searchTimeout.value = setTimeout(() => {
        performSearch(event);
      }, 300);
    };

    const filter = () => {};

    const addGroup = () => {
      showBatchUploadModal.value = true;
    };

    const openBatch = batchPk => {
      store.dispatch('fetchBatches', {
        page: currentPage.value,
        search: searchTerm.value,
        sort: currentSort.value,
        order: currentOrder.value,
      });
      router.push({ name: 'group-details', params: { id: batchPk.toString() } });
    };

    watch(fetchExcelListReportXLS, newVal => {
      fetchExcelListReportFile(newVal);
    });

    const handleAction = (option, row) => {
      switch (option.value) {
        case 'view':
          openBatch(row.batch_pk);
          break;
        case 'delete':
          deleteBatch(row.batch_pk);
          break;
        case 'download':
          requestExcelExport(row.batch_pk);
          break;
      }
    };

    const deleteBatch = async batchPk => {
      try {
        await axiosInstance.value.delete(`api/v1/batch/${batchPk}/`);
        store.dispatch('fetchBatches', {
          page: currentPage.value,
          search: searchTerm.value,
          sort: currentSort.value,
          order: currentOrder.value,
        });
        notify('success', 'Group Deleted', '', 3000);
      } catch (error) {
        if (error.response) {
          notify('warning', 'Error', error.response.data.error_message, 3000);
        }
      }
    };

    const requestExcelExport = async batch_pk => {
      await store.dispatch('fetchPartLibraryData', { batches: { batch_pk }, all_parts: true });
      batchParts.value = Object.fromEntries(partLibraryData.value.all_parts?.map(row => [row.part_id, true]) ?? []);
      await axiosInstance.value
        .post(`api/v1/excel-list-generator/`, {
          parts: batchParts.value,
        })
        .then(() => {
          notify('success', 'File requested', `Generating Excel File.`, 3000);
        });
    };

    const fetchExcelListReportFile = async newVal => {
      if (newVal) {
        await axiosInstance.value
          .get('api/v1/fetch-excel-list-generated/', {
            responseType: 'blob',
          })
          .then(response => {
            saveAs(response.data, 'part_list.xlsx');
          })
          .catch(error => {
            console.error(error);
          });
        store.commit('updateFetchExcelListReportXLS', false);
      }
    };

    const updateCurrentPage = page => {
      currentPage.value = page;
      store.dispatch('fetchBatches', {
        page: currentPage.value,
        search: searchTerm.value,
        sort: currentSort.value,
        order: currentOrder.value,
      });
    };

    store.dispatch('fetchBatches', {
      page: currentPage.value,
      search: searchTerm.value,
      sort: currentSort.value,
      order: currentOrder.value,
    });

    return {
      columns,
      dropdownOptions,
      selectedRows,
      currentPage,
      pageSize,
      inputPage,
      showBatchUploadModal,
      searchTerm,
      totalPages,
      pagedRows,
      startIdx,
      rows,
      endIdx,
      search,
      filter,
      addGroup,
      openBatch,
      handleAction,
      formatDate,
      updateCurrentPage,
      groupCountStart,
      groupCountEnd,
      batches,
      requestExcelExport,
      currentOrderDetails,
      showOrderDetailsModal,
      loadOrderDetails,
      batchSortOrder,
      currentSort,
      currentOrder,
    };
  },

  watch: {
    allOrderDetails: {
      handler(value) {
        if (value.length > 0) {
          this.calculateTotalOrderPrice();
        }
      },
      deep: true,
    },
  },

  methods: {
    ...mapMutations(['updateBatchSortOrder']),
    ...mapActions(['fetchBatches']),

    async openOrderDetailsModal(orderUid) {
      try {
        this.loadOrderDetails = true;
        this.showOrderDetailsModal = true;
        this.currentOrderDetails = {
          prp_parts: [],
          summary: [],
        };

        const response = await this.axiosInstance.get(`api/v1/prp-order-summary/${orderUid}/`);
        this.currentOrderDetails = response.data;

        this.currentOrderDetails.prp_parts = this.currentOrderDetails.prp_parts.map(part => {
          const chains = part.process_chains;
          const uniqueTexts = [...new Set(chains.map(chain => chain.custom_price_text).filter(text => text))];
          const numericChains = chains.filter(chain => !chain.custom_price_text);

          part.originalTexts = uniqueTexts;

          if (uniqueTexts.length === 1 && chains.every(chain => chain.custom_price_text === uniqueTexts[0])) {
            part.priceRangePerPart = uniqueTexts[0];
            part.priceRangePerLot = uniqueTexts[0];
          } else if (uniqueTexts.length > 1) {
            part.priceRangePerPart = 'Please refine your filters';
            part.priceRangePerLot = 'Please refine your filters';
          } else if (numericChains.length > 0) {
            const pricesPerLot = numericChains.map(chain => chain.market_price_pl).filter(Boolean);
            const pricesPerPart = numericChains.map(chain => chain.market_price_pp).filter(Boolean);

            part.priceRangePerPart = pricesPerPart.length
              ? `${this.$formatPrice(Math.min(...pricesPerPart))}${
                  pricesPerPart.length > 1 ? ` - ${this.$formatPrice(Math.max(...pricesPerPart))}` : ''
                }/Part`
              : 'Please refine your filters';

            part.priceRangePerLot = pricesPerLot.length
              ? `${this.$formatPrice(Math.min(...pricesPerLot))}${
                  pricesPerLot.length > 1 ? ` - ${this.$formatPrice(Math.max(...pricesPerLot))}` : ''
                }/Lot`
              : 'Please refine your filters';
          }

          return part;
        });

        const parts = this.currentOrderDetails.prp_parts;
        const partsWithOnlyText = parts.filter(
          part => part.originalTexts.length > 0 && part.process_chains.every(chain => chain.custom_price_text)
        );

        if (partsWithOnlyText.length === parts.length) {
          const allTexts = parts.flatMap(part => part.originalTexts);
          const uniqueTexts = [...new Set(allTexts)];

          if (uniqueTexts.length === 1) {
            this.currentOrderDetails.summary = [{ name: 'Total Price', value: uniqueTexts[0] }];
          } else if (uniqueTexts.length > 1) {
            this.currentOrderDetails.summary = [];
          }
        } else {
          const minTotal = this.currentOrderDetails.min_total_price;
          const maxTotal = this.currentOrderDetails.max_total_price;
          this.currentOrderDetails.summary = [
            {
              name: 'Total Price',
              value:
                minTotal !== maxTotal
                  ? `${this.$formatPrice(minTotal)} - ${this.$formatPrice(maxTotal)}`
                  : this.$formatPrice(minTotal),
            },
          ];
        }

        await Promise.all(
          this.currentOrderDetails.prp_parts.map(async part => {
            try {
              const response = await this.axiosInstance.get(`api/v1/part-thumbnail/${part.part_id}/`, {
                responseType: 'blob',
              });
              const fileReader = new FileReader();
              fileReader.readAsDataURL(response.data);
              fileReader.onload = e => {
                part.thumbnail = e.target.result || defaultImage;
              };
            } catch (error) {
              part.thumbnail = defaultImage;
            }

            try {
              const response = await this.axiosInstance.get(`/api/v1/prp-part/${part.part_id}/`);
              part.partDescription = response.data.part_description_items;
            } catch (error) {
              console.error('Error fetching part details:', error);
            }
          })
        );

        this.loadOrderDetails = false;
      } catch (error) {
        console.error('Error in openOrderDetailsModal:', error);
        this.$root.notify('warning', 'Order details not found', 3000);
      }
    },

    calculateTotalOrderPrice() {
      this.allOrderDetails.forEach(order => {
        order.minTotalPrice = order.prp_parts.reduce((acc, part) => acc + part.min_part_price_pl, 0);
      });

      this.allOrderDetails.forEach(order => {
        order.maxTotalPrice = order.prp_parts.reduce((acc, part) => acc + part.max_part_price_pl, 0);
      });

      this.allOrderDetails.forEach(order => {
        if (order.minTotalPrice !== order.maxTotalPrice) {
          order.summary = [
            {
              name: 'Total Price',
              value: `${this.$formatPrice(order.minTotalPrice)} - ${this.$formatPrice(order.maxTotalPrice)}`,
            },
          ];
        } else {
          order.summary = [
            {
              name: 'Total Price',
              value: this.$formatPrice(order.minTotalPrice),
            },
          ];
        }
      });
    },

    closeOrderDetailsModal() {
      this.showOrderDetailsModal = false;
      this.currentOrderUid = {};
    },

    sort(key) {
      this.currentSort = key;
      if (this.currentSort === key && this.currentOrder) {
        this.currentOrder = this.currentOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentOrder = 'asc';
      }

      this.fetchBatches({
        page: this.currentPage,
        search: this.searchTerm,
        sort: this.currentSort,
        order: this.currentOrder,
      });
      this.updateBatchSortOrder();
    },
  },
});
</script>
